
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface WritePostModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WritePostModal({ isOpen, onClose }: WritePostModalProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('free');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const categories = [
    { id: 'free', name: '자유게시판' },
    { id: 'tips', name: '꿀팁공유' },
    { id: 'review', name: '업장후기' },
    { id: 'qna', name: '질문답변' },
    { id: 'news', name: '업계소식' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setSubmitMessage('제목을 입력해주세요.');
      return;
    }
    if (!content.trim()) {
      setSubmitMessage('내용을 입력해주세요.');
      return;
    }
    if (content.length > 500) {
      setSubmitMessage('내용은 500자 이내로 작성해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('title', title);
      formData.append('category', category);
      formData.append('content', content);

      const response = await fetch('https://readdy.ai/api/form/d6bgp1itehdqnvnphtq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('게시글이 등록되었습니다!');
        setTimeout(() => {
          setTitle('');
          setCategory('free');
          setContent('');
          setSubmitMessage('');
          onClose();
        }, 1500);
      } else {
        setSubmitMessage('등록 중 오류가 발생했습니다.');
      }
    } catch {
      setSubmitMessage('등록 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-lg bg-dark-medium rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h3 className="text-lg font-bold text-white">새 글쓰기</h3>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-close-line w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} data-readdy-form id="community-post-form" className="p-6 space-y-5">
              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2">카테고리</label>
                <select
                  name="category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-neon-purple/50 transition-colors appearance-none cursor-pointer"
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id} className="bg-dark-medium">
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2">제목</label>
                <input
                  type="text"
                  name="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="제목을 입력하세요"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple/50 transition-colors"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2">
                  내용 <span className="text-gray-600">({content.length}/500)</span>
                </label>
                <textarea
                  name="content"
                  value={content}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setContent(e.target.value);
                  }}
                  maxLength={500}
                  rows={6}
                  placeholder="내용을 입력하세요"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-neon-purple/50 transition-colors resize-none"
                ></textarea>
              </div>

              {/* Message */}
              {submitMessage && (
                <p className={`text-sm ${submitMessage.includes('등록되었습니다') ? 'text-neon-green' : 'text-neon-pink'}`}>
                  {submitMessage}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 rounded-xl bg-gradient-neon text-white font-semibold text-sm hover:shadow-neon-purple transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? '등록 중...' : '게시하기'}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
